/**
 * useHistoryCapture
 *
 * Verbindet den Upload-Store mit der Dateihistorie, ohne beide eng zu koppeln: der Upload-Store
 * legt für jede erfolgreiche Operation ein **explizites, typisiertes Capture-Event**
 * ({@link HistoryCaptureEvent}: Absicht + Daten atomar) in seine `captureQueue`; dieses
 * Composable konsumiert die Queue **sequentiell** und reicht die Events an
 * {@link @/stores/useFileHistoryStore} weiter. Fehler in Dexie bleiben dort isoliert und
 * blockieren die Konvertierungs-Pipeline nie.
 *
 * Einmalig im `setup` von `App.vue` aufrufen – läuft unabhängig vom aktiven Tab.
 *
 * **Warum sequentiell (Promise-Kette)?** Ein `analysis`-Event wird erst verarbeitet, nachdem der
 * zugehörige Verlaufseintrag persistiert ist (der Eintrag entsteht durch das direkt davor
 * emittierte Event). Damit entfällt das frühere Polling (`setTimeout(50)×10`) in
 * `attachAnalysis` – die Reihenfolge ist strukturell garantiert statt zeitlich geraten.
 *
 * **Gruppierungsschlüssel ist die clientseitige `lineageId`** (nicht die Server-`file_id`), damit
 * Original + Transpositionen + Konvertierungen + manuelle Edits zur selben Originaldatei
 * gruppieren. Die Events tragen ihre `lineageId` selbst (zum Emit-Zeitpunkt eingefroren).
 */

import {watch} from 'vue';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import {useFileHistoryStore} from '@/stores/useFileHistoryStore';
import {griffschriftName} from '@/services/fileIdentity';
import type {HistoryCaptureEvent} from "@/stores/file.util.ts";

export function useHistoryCapture(): void {
    const uploadStore = useMusicUploadStore();
    const historyStore = useFileHistoryStore();

    async function dispatch(event: HistoryCaptureEvent): Promise<void> {
        switch (event.kind) {
            case 'upload':
                await historyStore.addUpload({
                    file: event.file,
                    serverFileId: event.lineageId,
                    filename: event.filename,
                    fileType: event.fileType,
                });
                return;

            case 'pdf-upload':
                // PDF: IMMER das nicht transponierte/konvertierte OMR-Ergebnis (MusicXML) sichern,
                // nicht die rohe PDF – so überspringt die Wiederverwendung das teure Audiveris.
                await historyStore.addUpload({
                    serverFileId: event.lineageId,
                    filename: event.filename,
                    musicXml: event.musicXml,
                    fileType: event.fileType,
                });
                return;

            case 'document':
                // Mehrteiliges PDF: pro Movement einen eigenen Upload-Eintrag anlegen (gemeinsame
                // documentId, je eigene Lineage). Nur das aktive Movement setzt den
                // currentEntryId-Zeiger.
                for (const m of event.movements) {
                    await historyStore.addUpload({
                        serverFileId: m.lineageId,
                        filename: m.filename,
                        musicXml: m.musicXml,
                        fileType: '.musicxml',
                        documentId: event.documentId,
                        movementIndex: m.movementIndex,
                        makeCurrent: m.lineageId === event.activeLineageId,
                        skipDedup: true,
                    });
                }
                return;

            case 'edited':
            case 'shifted':
            case 'merged':
            case 'mode-changed':
            case 'rebarred':
            case 'transposed':
                // Direkt in den gemeinsamen Sammelpunkt (die früheren Einzel-Wrapper waren
                // identische Delegationen).
                await historyStore.addDerived(event.kind, toDerivedInput(event));
                return;

            case 'converted':
                await historyStore.addConverted({
                    serverFileId: event.lineageId,
                    filename: griffschriftName(event.baseFilename),
                    musicXml: event.musicXml,
                    conversionId: event.conversionId,
                    semitones: event.semitones,
                    analysis: event.analysis,
                    // Snapshot der zur Konvertierung verwendeten Settings – reist im Event mit
                    // (zum Emit-Zeitpunkt eingefroren), statt hier live aus dem Settings-Store
                    // gelesen zu werden (der Stand könnte sich bis zur Verarbeitung ändern).
                    settings: event.settings,
                });
                return;

            case 'analysis':
                // Bevorzugt an den aktuellen Eintrag (Zeiger) anhängen – der wurde vom direkt
                // zuvor verarbeiteten Event gesetzt; Fallback: Match über (lineage, semitones).
                await historyStore.attachAnalysis(
                    event.lineageId,
                    event.semitones,
                    event.analysis,
                    uploadStore.currentEntryId ?? undefined,
                );
                return;
        }
    }

    function toDerivedInput(event: Extract<HistoryCaptureEvent, {
        kind: 'edited' | 'shifted' | 'merged' | 'mode-changed' | 'rebarred' | 'transposed'
    }>) {
        return {
            serverFileId: event.lineageId,
            filename: event.filename,
            musicXml: event.musicXml,
            semitones: event.semitones,
            fileType: event.fileType,
        };
    }

    // Sequentielle Verarbeitung: Events strikt in Emissions-Reihenfolge, jedes erst nach
    // Abschluss des vorherigen (z. B. Analyse nach persistiertem Eintrag).
    let chain: Promise<void> = Promise.resolve();

    watch(
        () => uploadStore.captureQueue,
        (queue) => {
            if (!queue.length) return;
            const events = uploadStore.drainCaptureQueue();
            for (const event of events) {
                chain = chain
                    .then(() => dispatch(event))
                    .catch((error) => {
                        // Capture darf die Pipeline nie blockieren – nur protokollieren.
                        console.warn('Dateihistorie: Capture-Event fehlgeschlagen.', event.kind, error);
                    });
            }
        },
        {immediate: true},
    );
}
